import React, { useState, useEffect, ReactElement } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  Paper,
  Grid,
  makeStyles,
  Typography,
  TableCell,
  Button,
  Divider,
  Link,
} from "@material-ui/core";
import clsx from "clsx";
import useInput from "../../../hooks/input/useInput";
import useTable from "../../../hooks/table/useTable";
import TrTable from "../../../components/shared/controls/TrTable";
import TrSelect from "../../shared/controls/TrSelect";
import TrSelectData from "../../../models/shared/TrSelectData";
import TrAutocomplete from "../../../components/shared/controls/TrAutocomplete";
import LinkAdapter from "../../../components/shared/common/LinkAdapter";
import PagedResult from "../../../models/shared/PagedResult";
import TrTableHeadMetadata from "../../../models/shared/TrTableHeadMetadata";
import TrTableBodyData from "../../../models/shared/TrTableBodyData";
import FinancingRequestList from "../../../models/admin/financing-request/FinancingRequestList";
import FinancingRequestApiClient from "../../../api/admin/FinancingRequestApiClient";
import FinancingRequestStatusEnum from "../../../models/financing-request-status/FinancingRequestStatusEnum";
import YearFilter from "../../../models/shared/YearFilter";
import { nameof } from "../../../utilities/utilities";
import { adminFinancingRequestDetailsUrl } from "../../../urls";
import FinancingRequestFilter from "../../../models/admin/financing-request/FinancingRequestFilter";
import FinancingRequestFilterValue from "../../../models/admin/financing-request/FinancingRequestFilterValue";
import SelectItem from "../../../models/shared/SelectItem";
import FinancingRequestStatusElement from "../../shared/controls/FinancingRequestStatusElement";

export default function FinancingRequestListPage({
  history,
}: RouteComponentProps) {
  const classes = useStyles({});

  const defaultFilterValues = {
    schoolYearId: "",
    schoolId: "",
    status: "",
    schoolTypeId: "",
  };
  const [filters, setFilters] = useState<FinancingRequestFilter>({
    schools: [],
    schoolYears: [],
    statuses: [],
    schoolTypes: [],
  });


  const [filterValue, setFilterValue] = useState<FinancingRequestFilterValue>(() => {
    const savedFilterValues = sessionStorage.getItem('adminFinancingRequestFilterValues');
    return savedFilterValues ? JSON.parse(savedFilterValues) : defaultFilterValues;
  });


  const [financingRequestsPaged, setFinancingRequestsPaged] = useState<
    PagedResult<FinancingRequestList>
  >({} as PagedResult<FinancingRequestList>);

  const { page, pageSize, handleChangePage, handleChangepageSize } = useTable();
  const { handleSelectChange } = useInput();

  useEffect(() => {
    getFilters();
  }, []);


  useEffect(() => {
    sessionStorage.setItem('adminFinancingRequestFilterValues', JSON.stringify(filterValue));
  }, [filterValue]);

  const clearFilters = () => {
    setFilterValue(defaultFilterValues);
    sessionStorage.setItem('adminFinancingRequestFilterValues', JSON.stringify(defaultFilterValues));
  };


  useEffect(() => {
    search();
  }, [filterValue, page, pageSize]);

  async function search() {
    const apiClient = new FinancingRequestApiClient();
    const requests = await apiClient.search(filterValue, page + 1, pageSize);
    setFinancingRequestsPaged(requests);
  }

  async function getFilters() {
    const apiClient = new FinancingRequestApiClient();
    try {
      const searchFilters = await apiClient.getSearchFilters();
      setFilters(searchFilters);

      const savedFilterValues = sessionStorage.getItem('adminFinancingRequestFilterValues');
      if (savedFilterValues) {
        setFilterValue(JSON.parse(savedFilterValues));
      } else {
        setFilterValue(defaultFilterValues);
      }
    } catch (error) {
      console.error('Failed to fetch filters:', error);
      setFilters({
        schools: [],
        schoolYears: [],
        statuses: [],
        schoolTypes: [],
      });
      setFilterValue(defaultFilterValues);
    }
  }

  function convertToISODate(dateString: string): string {
    dateString = dateString.trim().replace(/\.$/, "");
    const parts = dateString.split(".");
    if (parts.length !== 3) {
      return "";
    }
    const day = parts[0].padStart(2, "0");
    const month = parts[1].padStart(2, "0");
    const year = parts[2];
    return `${year}-${month}-${day}`;
  }

  function formatDate(dateString: string | null): string {
    if (!dateString) return "";
    const isoDateString = convertToISODate(dateString);
    if (!isoDateString) return dateString;
    const date = new Date(isoDateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }
    const day = ("0" + date.getDate()).slice(-2);
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    return `${day}.${month}.${year}.`;
  }

  const tableHeadMetadata: TrTableHeadMetadata<FinancingRequestList>[] = [
    { id: "schoolYearName", label: "Šk. godina", dataName: "schoolYearName" },
    { id: "schoolName", label: "Škola", dataName: "schoolName" },
    {
      id: "classificationMark",
      label: "Klasa",
      dataName: "classificationMark",
    },
    {
      id: "registerNumber",
      label: "Urudžbeni broj",
      dataName: "registerNumber",
    },
    { id: "studentsNumber", label: "Broj učenika", dataName: "studentsNumber" },
    {
      id: "submissionDate",
      label: "Datum predaje zahtjeva",
      dataName: "submissionDate",
    },
    {
      id: "validFrom",
      label: "Datum od kada vrijedi zahtjev",
      dataName: "validFrom",
    },
    {
      id: "validTo",
      label: "Datum do kada vrijedi zahtjev",
      dataName: "validTo",
    },
    {
      id: "status",
      label: "Status",
      dataName: "statusName",
      dataFunc: (f: FinancingRequestList) => (
        <FinancingRequestStatusElement
          statusId={f.statusId}
          statusName={f.statusName}
        />
      ),
    },
    {
      id: "action",
      label: "",
      dataFunc: (f: FinancingRequestList) => (
        <Button
          variant="outlined"
          size="small"
          color="primary"
          component={LinkAdapter}
          to={adminFinancingRequestDetailsUrl(f.id.toString())}
        >
          PREGLED
        </Button>
      ),
    },
  ];

  const tableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: financingRequestsPaged.data,
  };

  const handleSchoolChange = (name: string, value: any) => {
    setFilterValue((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBodyCellCreated = (
    tableCell: ReactElement,
    rowData: FinancingRequestList,
    cellData: any,
    headMetadata?: TrTableHeadMetadata<FinancingRequestList>
  ) => {
    let statusClassName = "";
    switch (rowData.statusId) {
      case FinancingRequestStatusEnum.Submitted:
        statusClassName = clsx(classes.bold, classes.warning);
        break;
      case FinancingRequestStatusEnum.Approved:
        statusClassName = clsx(classes.bold, classes.success);
        break;
      case FinancingRequestStatusEnum.Rejected:
        statusClassName = clsx(classes.bold, classes.error);
        break;
      default:
        break;
    }

    if (headMetadata && headMetadata.dataName === "submissionDate") {

      return (
        <TableCell
          key={tableCell.key}
          {...tableCell.props}
          className={clsx(tableCell.props.className, {
            [classes.redText]: rowData.isLateSubmition,
          })}
        >
          {formatDate(rowData.submissionDate)}
        </TableCell>
      );
    }

    if (headMetadata && !headMetadata.dataName) {
      return (
        <TableCell key={tableCell.key} {...tableCell.props}>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            component={LinkAdapter}
            to={adminFinancingRequestDetailsUrl(rowData.id.toString())}
          >
            PREGLED
          </Button>
        </TableCell>
      );
    } else {
      return (
        <TableCell
          key={tableCell.key}
          {...tableCell.props}
          className={clsx(
            tableCell.props.className,
            headMetadata &&
            headMetadata.dataName === nameof<typeof rowData>("statusName") &&
            statusClassName
          )}
        />
      );
    }
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5">Odobravanje godišnjih zahtjeva</Typography>
        </Grid>
        <Grid item xs={12} md={3}>
          <TrSelect
            label={"Šk. godina"}
            value={filterValue.schoolYearId}
            name={nameof<typeof filterValue>("schoolYearId")}
            fullWidth={true}
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: filters.schoolYears || [],
              } as TrSelectData<YearFilter>
            }
            onChange={(e) => handleSelectChange(e, setFilterValue)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TrAutocomplete
            label="Škola"
            name={nameof<typeof filterValue>("schoolId")}
            data={filters.schools}
            value={filterValue.schoolId}
            minimumInputLength={3}
            onChange={handleSchoolChange}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TrSelect
            label={"Status"}
            value={filterValue.status}
            name={nameof<typeof filterValue>("status")}
            fullWidth={true}
            selectData={
              {
                valueProp: "value",
                textProp: "text",
                data: filters.statuses || [],
              } as TrSelectData<SelectItem<any>>
            }
            onChange={(e) => handleSelectChange(e, setFilterValue)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TrSelect
            label={"Osnovna/Srednja škola"}
            value={filterValue.schoolTypeId}
            name={nameof<typeof filterValue>("schoolTypeId")}
            fullWidth={true}
            selectData={
              {
                valueProp: "value",
                textProp: "text",
                data: filters.schoolTypes || [],
              } as TrSelectData<SelectItem<any>>
            }
            onChange={(e) => handleSelectChange(e, setFilterValue)}
          />
        </Grid>
        <Grid item xs={12} style={{ textAlign: 'right' }}>
          <Link component="button" onClick={clearFilters} variant="body2" style={{ cursor: 'pointer' }}>
            Očisti filtere
          </Link>
        </Grid>
        <Grid item xs={12}>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Paper>
            <TrTable
              hover={true}
              headMetadata={tableHeadMetadata}
              bodyData={tableBodyData}
              enablePagination={true}
              page={page}
              pageSize={pageSize}
              totalCount={financingRequestsPaged.count}
              onChangePage={handleChangePage}
              onChangePageSize={handleChangepageSize}
              noDataMessage={"Trenutno nema godišnjih zahtjeva"}
              onBodyCellCreated={handleBodyCellCreated}
            />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  success: {
    color: "#43A047",
  },
  warning: {
    color: "#FFA047",
  },
  error: {
    color: theme.palette.error.main,
  },
  bold: {
    fontWeight: "bold",
  },
  redText: {
    color: "#FF0000",
  },
}));
